/**
 * CaribbeanLeaf Customer Authentication Service
 *
 * MIRRORS: caribbeanleaf-api/src/customers/customer_controller.py
 *
 * Handles customer authentication separate from staff auth.
 * Uses CUSTOMER_TOKEN_KEY to prevent token collision with staff portal.
 */

import { API_URL } from '@/config/api';
import { setTenantSlug, getTenantSlug } from '@/lib/api-client';
import type {
  Customer,
  CustomerLoginRequest,
  CustomerLoginResponse,
  CustomerRegisterRequest,
  CustomerRegisterResponse,
  CustomerVerifyMedicalCardRequest,
  CustomerSubmitTouristPermitRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '@/types/customer';

// ─────────────────────────────────────────────────────────────────────────────
// Storage Keys - CRITICAL: Different from staff TOKEN_KEY
// ─────────────────────────────────────────────────────────────────────────────

export const CUSTOMER_TOKEN_KEY = 'caribbeanleaf_customer_token';
export const CUSTOMER_REFRESH_TOKEN_KEY =
  'caribbeanleaf_customer_refresh_token';

// ─────────────────────────────────────────────────────────────────────────────
// Token Management
// ─────────────────────────────────────────────────────────────────────────────

export function getCustomerToken(): string | null {
  return localStorage.getItem(CUSTOMER_TOKEN_KEY);
}

export function getCustomerRefreshToken(): string | null {
  return localStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY);
}

export function setCustomerTokens(
  accessToken: string,
  refreshToken?: string
): void {
  localStorage.setItem(CUSTOMER_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(CUSTOMER_REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function clearCustomerTokens(): void {
  localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  localStorage.removeItem(CUSTOMER_REFRESH_TOKEN_KEY);
}

// ─────────────────────────────────────────────────────────────────────────────
// API Error
// ─────────────────────────────────────────────────────────────────────────────

export class CustomerAuthError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'CustomerAuthError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Request Helper
// ─────────────────────────────────────────────────────────────────────────────

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
}

async function customerRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  authenticated = false
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (authenticated) {
    const token = getCustomerToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Send tenant context for multi-tenant resolution
  const tenantSlug = getTenantSlug();
  if (tenantSlug) {
    headers['X-Tenant-ID'] = tenantSlug;
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch {
    throw new CustomerAuthError(
      'Invalid response from server',
      response.status,
      'PARSE_ERROR'
    );
  }

  if (!response.ok || !data.success) {
    throw new CustomerAuthError(
      data.message || 'Request failed',
      response.status,
      data.code || 'API_ERROR',
      data.details
    );
  }

  return data.data as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth Service
// ─────────────────────────────────────────────────────────────────────────────

export const customerAuthService = {
  /**
   * Register a new customer
   */
  async register(
    request: CustomerRegisterRequest
  ): Promise<CustomerRegisterResponse> {
    return customerRequest<CustomerRegisterResponse>(
      'POST',
      '/customers/auth/register',
      request
    );
  },

  /**
   * Login with email and password
   */
  async login(request: CustomerLoginRequest): Promise<CustomerLoginResponse> {
    const response = await customerRequest<CustomerLoginResponse>(
      'POST',
      '/customers/auth/login',
      request
    );

    // Store tokens and tenant context
    setCustomerTokens(response.access_token, response.refresh_token);
    if (response.tenant_slug) {
      setTenantSlug(response.tenant_slug);
    }

    return response;
  },

  /**
   * Logout and clear tokens
   */
  async logout(): Promise<void> {
    // Revoke refresh token server-side before clearing local tokens
    const refreshToken = getCustomerRefreshToken();
    if (refreshToken) {
      try {
        await customerRequest('POST', '/customers/auth/logout', {
          refresh_token: refreshToken,
        });
      } catch {
        // Network error — still clear tokens below (defensive)
      }
    }
    clearCustomerTokens();
  },

  /**
   * Get current customer profile
   */
  async getProfile(): Promise<Customer> {
    return customerRequest<Customer>('GET', '/customers/me', undefined, true);
  },

  /**
   * Refresh the access token
   */
  async refreshToken(): Promise<CustomerLoginResponse> {
    const refreshToken = getCustomerRefreshToken();

    if (!refreshToken) {
      throw new CustomerAuthError(
        'No refresh token available',
        401,
        'NO_REFRESH_TOKEN'
      );
    }

    const response = await customerRequest<CustomerLoginResponse>(
      'POST',
      '/customers/auth/refresh',
      { refresh_token: refreshToken }
    );

    setCustomerTokens(response.access_token, response.refresh_token);

    return response;
  },

  /**
   * Verify medical card
   */
  async verifyMedicalCard(
    request: CustomerVerifyMedicalCardRequest
  ): Promise<{ message: string; verification_status: string }> {
    return customerRequest<{ message: string; verification_status: string }>(
      'POST',
      '/customers/auth/verify/medical',
      request,
      true
    );
  },

  /**
   * Submit tourist permit for verification
   */
  async verifyTouristPermit(
    request: CustomerSubmitTouristPermitRequest
  ): Promise<{ message: string; verification_status: string }> {
    return customerRequest<{ message: string; verification_status: string }>(
      'POST',
      '/customers/auth/verify/tourist',
      request,
      true
    );
  },

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<{ message: string }> {
    return customerRequest<{ message: string }>(
      'POST',
      '/customers/auth/resend-verification',
      undefined,
      true
    );
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    return customerRequest<{ message: string }>(
      'POST',
      '/customers/auth/verify-email',
      {
        token,
      }
    );
  },

  /**
   * Request password reset
   */
  async forgotPassword(
    request: ForgotPasswordRequest
  ): Promise<{ message: string }> {
    return customerRequest<{ message: string }>(
      'POST',
      '/customers/auth/forgot-password',
      request
    );
  },

  /**
   * Reset password with token
   */
  async resetPassword(
    request: ResetPasswordRequest
  ): Promise<{ message: string }> {
    return customerRequest<{ message: string }>(
      'POST',
      '/customers/auth/reset-password',
      request
    );
  },
};

export default customerAuthService;
